import { ApiService, type ApiRequestConfig } from "./api.service";
import type { TIssueComment, TIssueCommentReaction } from "@/types/issue-comment";

export class IssueCommentService extends ApiService {
  async listComments(
    slug: string,
    projectId: string,
    issueId: string,
    config?: ApiRequestConfig,
  ): Promise<TIssueComment[]> {
    return this.get(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/comments/`,
      config,
    );
  }

  async createComment(
    slug: string,
    projectId: string,
    issueId: string,
    data: Partial<TIssueComment>,
  ): Promise<TIssueComment> {
    return this.post(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/comments/`,
      data,
    );
  }

  /** List comments on a **project-less** work item (an app-managed event /
   *  Session), addressed at workspace scope. */
  async listWorkspaceComments(
    slug: string,
    issueId: string,
    config?: ApiRequestConfig,
  ): Promise<TIssueComment[]> {
    return this.get(`/api/workspaces/${slug}/work-items/${issueId}/comments/`, config);
  }

  /** Create a comment on a project-less work item, at workspace scope. */
  async createWorkspaceComment(
    slug: string,
    issueId: string,
    data: Partial<TIssueComment>,
  ): Promise<TIssueComment> {
    return this.post(`/api/workspaces/${slug}/work-items/${issueId}/comments/`, data);
  }

  async updateComment(
    slug: string,
    projectId: string,
    issueId: string,
    commentId: string,
    data: Partial<TIssueComment>,
  ): Promise<TIssueComment> {
    return this.patch(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/comments/${commentId}/`,
      data,
    );
  }

  async deleteComment(
    slug: string,
    projectId: string,
    issueId: string,
    commentId: string,
  ): Promise<void> {
    return this.delete(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/comments/${commentId}/`,
    );
  }

  async listReactions(
    slug: string,
    projectId: string,
    commentId: string,
  ): Promise<TIssueCommentReaction[]> {
    return this.get(
      `/api/workspaces/${slug}/projects/${projectId}/comments/${commentId}/reactions/`,
    );
  }

  async addReaction(
    slug: string,
    projectId: string,
    commentId: string,
    data: { reaction: string },
  ): Promise<TIssueCommentReaction> {
    return this.post(
      `/api/workspaces/${slug}/projects/${projectId}/comments/${commentId}/reactions/`,
      data,
    );
  }

  async removeReaction(
    slug: string,
    projectId: string,
    commentId: string,
    reactionCode: string,
  ): Promise<void> {
    return this.delete(
      `/api/workspaces/${slug}/projects/${projectId}/comments/${commentId}/reactions/${reactionCode}/`,
    );
  }
}
